import { Box, Typography, IconButton } from '@mui/material';
import { observer } from 'mobx-lite';
import { useEffect, useRef, useState } from 'react';
import { Delete, OpenInFull, CloseFullscreen } from '@mui/icons-material';
import { debugLog, type DebugLogEntry } from '../Stores/DebugLogStore';
import { MainStoreContextItem } from '../Stores/MainStore';

const levelColor: Record<DebugLogEntry['level'], string> = {
    info: 'text.secondary',
    warn: 'warning.main',
    error: 'error.main',
};

const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })
        + '.' + String(date.getMilliseconds()).padStart(3, '0');
};

const DebugLogPanel: React.FC = observer(() => {
    const store = MainStoreContextItem.useStore();
    const { settingsStore } = store;
    const viewport = useRef<HTMLDivElement>(null);
    const [maximized, setMaximized] = useState(false);
    const entryCount = debugLog.entries.length;

    useEffect(() => {
        requestAnimationFrame(() => {
            if (viewport.current) {
                viewport.current.scrollTop = viewport.current.scrollHeight;
            }
        });
    }, [entryCount]);

    if (!settingsStore.showDebugLog) {
        return null;
    }

    return (
        <Box
            sx={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 999,
                bgcolor: '#1a1a2e',
                borderTop: '1px solid #333',
                fontFamily: 'monospace',
                fontSize: '11px',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: 1,
                    py: 0.25,
                    borderBottom: '1px solid #444',
                }}
            >
                <Typography variant="caption" fontWeight={600} color="text.secondary">
                    Debug Log ({debugLog.entries.length})
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton
                       
                        onClick={() => setMaximized((m) => !m)}
                        title={maximized ? 'Restore' : 'Maximize'}
                        sx={{ p: 0.25 }}
                    >
                        {maximized ? <CloseFullscreen sx={{ fontSize: 16 }} /> : <OpenInFull sx={{ fontSize: 14 }} />}
                    </IconButton>
                    <IconButton
                      
                        onClick={() => {
                            if (debugLog.entries.length === 0 || window.confirm('Clear all debug log entries?')) {
                                debugLog.clear();
                            }
                        }}
                        title="Clear log"
                        sx={{ p: 0.25 }}
                    >
                        <Delete sx={{ fontSize: 16 }} />
                    </IconButton>
                </Box>
            </Box>
            <Box
                ref={viewport}
                sx={{
                    height: maximized ? 'calc(100dvh - 120px)' : 120,
                    overflow: 'auto',
                    px: 1,
                    py: 0.25,
                    '&::-webkit-scrollbar': { width: 6 },
                    '&::-webkit-scrollbar-thumb': { bgcolor: '#555', borderRadius: 3 },
                }}
            >
                {debugLog.entries.length === 0 ? (
                    <Typography variant="caption" color="text.secondary" fontStyle="italic">
                        No log entries yet
                    </Typography>
                ) : (
                    debugLog.entries.map((entry) => (
                        <Typography
                            key={entry.id}
                            variant="caption"
                            component="div"
                            sx={{ color: levelColor[entry.level], lineHeight: 1.4, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
                        >
                            <Typography component="span" variant="caption" color="text.secondary">
                                {formatTime(entry.timestamp)}
                            </Typography>{' '}
                            {entry.level !== 'info' && (
                                <Typography component="span" variant="caption" fontWeight={700} sx={{ color: levelColor[entry.level] }}>
                                    [{entry.level.toUpperCase()}]{' '}
                                </Typography>
                            )}
                            {entry.message}
                        </Typography>
                    ))
                )}
            </Box>
        </Box>
    );
});

export default DebugLogPanel;
